import { NEXT_PUBLIC_GRAPHQL_URL } from '@jusbrasil-web/shared-apollo-client'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { IS_DEV_ENVIRONMENT } from '../../constants'

export type UseSessionCheckProps = {
  /**
   * @description Paths from the current app to skip session check
   * @note Be careful with this prop, because it will skip session check and the user will be able to access the page without having a valid session
   * @example ['/'] - Skip session check for all paths from the current app
   * @example ['/assinar'] - Skip session check for a specific path
   * @default []
   */
  urlPathsToSkip?: string[] | ((path: string) => boolean)
  auto?: boolean
}

const isFunction = (value: unknown): value is (path: string) => boolean => {
  return typeof value === 'function'
}

export const useSessionCheck = ({
  auto = true,
  urlPathsToSkip = [],
}: UseSessionCheckProps = {}) => {
  const router = useRouter()
  const controllerRef = useRef<AbortController | null>(null)

  const shouldSkip = useMemo(() => {
    if (isFunction(urlPathsToSkip)) {
      return urlPathsToSkip(router.pathname)
    }

    return urlPathsToSkip.some((path) => router.pathname.startsWith(path))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname])

  const validateSession = useCallback(async () => {
    try {
      if (IS_DEV_ENVIRONMENT || shouldSkip) return

      controllerRef.current?.abort()

      controllerRef.current = new AbortController()

      const response = await fetch(NEXT_PUBLIC_GRAPHQL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controllerRef.current.signal,
        body: JSON.stringify({
          query: `
              query SessionCheck {
                root {
                  session {
                    check {
                      mustRedirectTo
                    }
                  }
                }
              }
            `,
        }),
      })

      if (controllerRef.current.signal.aborted) return
      if (!response.ok) throw new Error(`Network error: ${response.status}`)

      const parsedResponse = await response.json()

      if (parsedResponse?.errors?.length) {
        const errorMessage = parsedResponse?.errors.reduce(
          (acc: string, error: { message: string }) => {
            acc += `\n${error.message}`
            return acc
          },
          ''
        )

        throw new Error(errorMessage)
      }

      const redirectTo = parsedResponse?.data?.root?.session?.check?.mustRedirectTo

      if (redirectTo) {
        console.log(`Redirecting to ${redirectTo} due to session validation`)

        window.location.assign(redirectTo)
      }
    } catch (error) {
      console.error('[shared-app-root-provider] Error trying to validate session', error)
    }
  }, [shouldSkip])

  useEffect(() => {
    if (!auto) return

    validateSession()

    return () => {
      controllerRef.current?.abort()
    }
  }, [auto, validateSession])

  return { validateSession }
}
